import { atom } from 'jotai';

import type { MinimalCard } from '@/services/cardDatabase';

import type { ArchetypeScore, ColorOpenness, DraftSession, DraftStats } from '../types';

// --- Core session atom ---

export const draftSessionAtom = atom<DraftSession | null>(null);

// --- Derived: current pack for the player ---

export const currentPackAtom = atom<MinimalCard[]>((get) => {
  const session = get(draftSessionAtom);
  if (!session || session.status !== 'picking') return [];

  const packRound = session.packs[session.currentPackIndex];
  if (!packRound) return [];

  // Player always uses pack slot 0 (rotated to them)
  const playerPack = packRound[0];
  return playerPack?.cards ?? [];
});

// --- Derived: draft stats from player pool ---

export const draftStatsAtom = atom<DraftStats>((get) => {
  const session = get(draftSessionAtom);
  const pool = session?.playerPool ?? [];

  const manaCurve: Record<number, number> = {};
  const colorDistribution: Record<string, number> = { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 };
  const typeBreakdown: Record<string, number> = {};
  let creatureCount = 0;
  let totalCMC = 0;
  let nonLandCount = 0;

  for (const card of pool) {
    // Mana curve
    const cmc = Math.min(card.cmc ?? 0, 7); // Group 7+ together
    manaCurve[cmc] = (manaCurve[cmc] || 0) + 1;

    // Color distribution
    if (card.color_identity.length === 0) {
      colorDistribution['C'] = (colorDistribution['C'] || 0) + 1;
    } else {
      for (const color of card.color_identity) {
        colorDistribution[color] = (colorDistribution[color] || 0) + 1;
      }
    }

    // Type breakdown
    const typeLine = card.type_line?.toLowerCase() ?? '';
    if (typeLine.includes('creature')) {
      typeBreakdown['Creature'] = (typeBreakdown['Creature'] || 0) + 1;
      creatureCount++;
    } else if (typeLine.includes('instant')) {
      typeBreakdown['Instant'] = (typeBreakdown['Instant'] || 0) + 1;
    } else if (typeLine.includes('sorcery')) {
      typeBreakdown['Sorcery'] = (typeBreakdown['Sorcery'] || 0) + 1;
    } else if (typeLine.includes('enchantment')) {
      typeBreakdown['Enchantment'] = (typeBreakdown['Enchantment'] || 0) + 1;
    } else if (typeLine.includes('artifact')) {
      typeBreakdown['Artifact'] = (typeBreakdown['Artifact'] || 0) + 1;
    } else if (typeLine.includes('planeswalker')) {
      typeBreakdown['Planeswalker'] = (typeBreakdown['Planeswalker'] || 0) + 1;
    } else if (typeLine.includes('land')) {
      typeBreakdown['Land'] = (typeBreakdown['Land'] || 0) + 1;
    } else {
      typeBreakdown['Other'] = (typeBreakdown['Other'] || 0) + 1;
    }

    // Average CMC (exclude lands)
    if (!typeLine.includes('land')) {
      totalCMC += card.cmc ?? 0;
      nonLandCount++;
    }
  }

  return {
    manaCurve,
    colorDistribution,
    typeBreakdown,
    creatureCount,
    averageCMC: nonLandCount > 0 ? totalCMC / nonLandCount : 0,
    totalPicks: pool.length,
  };
});

// --- Derived: color openness signals ---

export const colorOpennessAtom = atom<ColorOpenness>((get) => {
  const session = get(draftSessionAtom);
  if (!session) return { W: 0.5, U: 0.5, B: 0.5, R: 0.5, G: 0.5 };

  const aiPlayers = session.aiPlayers;
  if (aiPlayers.length === 0) return { W: 0.5, U: 0.5, B: 0.5, R: 0.5, G: 0.5 };

  // Count how many AI players are committed to each color (primary or secondary)
  const colorDrafters: Record<string, number> = { W: 0, U: 0, B: 0, R: 0, G: 0 };

  for (const ai of aiPlayers) {
    if (ai.colorCommitment.primary) {
      colorDrafters[ai.colorCommitment.primary] = (colorDrafters[ai.colorCommitment.primary] || 0) + 1;
    }
    if (ai.colorCommitment.secondary) {
      colorDrafters[ai.colorCommitment.secondary] = (colorDrafters[ai.colorCommitment.secondary] || 0) + 1;
    }
  }

  // Openness = inverse of how many drafters are in that color
  // With 7 AI, expect ~2.8 per color on average (14 color slots / 5 colors)
  // 0 drafters = wide open (1.0), 1 = open (0.75), 2 = slightly open (0.55),
  // 3 = contested (0.4), 4+ = cut (0.2)
  const numAI = aiPlayers.length;
  const expectedPerColor = (numAI * 2) / 5; // Each AI has 2 colors

  function computeOpenness(drafterCount: number): number {
    if (drafterCount === 0) return 1.0;
    const ratio = drafterCount / expectedPerColor;
    // Smoothly scale: ratio=1 → 0.5 (average), ratio=2 → ~0.15, ratio=0.5 → ~0.75
    return Math.max(0.05, 1 - ratio * 0.45);
  }

  return {
    W: computeOpenness(colorDrafters['W']),
    U: computeOpenness(colorDrafters['U']),
    B: computeOpenness(colorDrafters['B']),
    R: computeOpenness(colorDrafters['R']),
    G: computeOpenness(colorDrafters['G']),
  };
});

// --- Derived: pick recommendation ---

export const pickRecommendationAtom = atom<{ card: MinimalCard; reason: string }[]>((get) => {
  const session = get(draftSessionAtom);
  const pack = get(currentPackAtom);

  if (!session || pack.length === 0) return [];

  const playerColors = getTopColors(session.playerPool);
  const pickCount = session.playerPool.length;
  // How committed are we? Early = flexible, late = locked in
  const isCommitted = pickCount >= 6;
  const isLocked = pickCount >= 14;

  const scored = pack.map((card) => {
    let score = 0;
    let reason = '';

    const typeLine = card.type_line?.toLowerCase() ?? '';
    const cardColors = card.color_identity;
    const isColorless = cardColors.length === 0;
    const isOnColor = isColorless || cardColors.some((c) => playerColors.includes(c));
    const isFullyOnColor = isColorless || cardColors.every((c) => playerColors.includes(c));

    // Base score from rarity
    if (card.rarity === 'mythic') {
      score += 4.5;
      reason = 'Bomb (mythic)';
    } else if (card.rarity === 'rare') {
      score += 3.5;
      reason = 'Bomb (rare)';
    }

    // Planeswalkers
    if (typeLine.includes('planeswalker')) {
      score += 2;
      reason = 'Bomb (planeswalker)';
    }

    // Removal (instants/sorceries at uncommon+)
    if ((typeLine.includes('instant') || typeLine.includes('sorcery')) && card.rarity !== 'common') {
      score += 2;
      if (!reason) reason = 'Removal (uncommon+)';
    }

    // Creatures
    if (typeLine.includes('creature')) {
      score += 0.5;
      if (card.rarity === 'uncommon') score += 1;
      if (!reason) reason = 'Creature';
    }

    // Color alignment — this is the critical part
    if (isCommitted && playerColors.length > 0) {
      if (isFullyOnColor) {
        score += isLocked ? 3 : 2;
        if (!reason) reason = 'On-color playable';
      } else if (isOnColor) {
        // Partially on-color (multicolor with one matching)
        score += isLocked ? 1 : 1;
        if (!reason) reason = 'Partially on-color';
      } else if (!isColorless) {
        // Off-color penalty — gets stronger as draft progresses
        const penalty = isLocked ? -4 : -2.5;
        score += penalty;
        reason = reason ? `${reason} (off-color)` : 'Off-color';
      }
    }

    if (!reason) reason = 'Playable';

    return { card, score, reason };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 3).map((s) => ({ card: s.card, reason: s.reason }));
});

function getTopColors(pool: MinimalCard[]): string[] {
  const counts: Record<string, number> = {};
  for (const card of pool) {
    for (const color of card.color_identity) {
      counts[color] = (counts[color] || 0) + 1;
    }
  }
  return Object.entries(counts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 2)
    .map(([color]) => color);
}

// --- Derived: archetype scores ---

export const archetypeScoresAtom = atom<ArchetypeScore[]>((get) => {
  const session = get(draftSessionAtom);
  if (!session || session.playerPool.length < 3) return [];

  const pool = session.playerPool;
  const colorCounts: Record<string, number> = {};

  for (const card of pool) {
    for (const color of card.color_identity) {
      colorCounts[color] = (colorCounts[color] || 0) + 1;
    }
  }

  // Generate all 2-color pair scores
  const colors = ['W', 'U', 'B', 'R', 'G'];
  const pairs: ArchetypeScore[] = [];

  for (let i = 0; i < colors.length; i++) {
    for (let j = i + 1; j < colors.length; j++) {
      const c1 = colors[i];
      const c2 = colors[j];
      const count = (colorCounts[c1] || 0) + (colorCounts[c2] || 0);
      const total = pool.length || 1;
      const score = count / total;

      pairs.push({
        archetype: { colors: [c1, c2], name: `${c1}${c2}`, keywords: [] },
        score,
      });
    }
  }

  pairs.sort((a, b) => b.score - a.score);
  return pairs.slice(0, 3);
});
